import { IApiSupersetDataset, IApiSupersetFormData, SupersetColumnType } from '@cbridge-packages/api-superset';

/**
 * Searchable column with optional SQL expression for virtual columns
 */
export interface ISearchableColumn {
  /** Column name (for regular columns) or label (for virtual columns) */
  columnName: string;
  /** SQL expression (only for virtual columns) */
  expression?: string;
}

/**
 * Filters dataset columns to find searchable ones for quick filter
 * Returns both regular columns and filterable virtual columns
 *
 * IMPORTANT: In aggregate mode (with GROUP BY), only columns from groupby are available
 * after aggregation. All other columns are lost after GROUP BY and cannot be used in HAVING/WHERE.
 *
 * @param dataset - Dataset metadata
 * @param formData - Form data with query_mode and groupby
 * @returns Searchable columns (limited to groupby in aggregate mode)
 */
export const getSearchableColumnsDetailed = (
  dataset: IApiSupersetDataset,
  formData: IApiSupersetFormData,
): ISearchableColumn[] => {
  const allSearchableColumns = dataset.columns
    .filter((col) =>
      col.type_generic === SupersetColumnType.STRING &&
      !col.type?.startsWith('Enum') &&
      // Include regular columns OR filterable virtual columns
      (!col.expression || col.filterable),
    )
    .map((col) => ({
      columnName: col.column_name,
      expression: col.expression || undefined,
    }));

  // In aggregate mode, only columns from groupby are available after GROUP BY
  if (formData.query_mode === 'aggregate' && formData.groupby?.length) {
    const groupBySet = new Set(formData.groupby);
    return allSearchableColumns.filter((col) => groupBySet.has(col.columnName));
  }

  return allSearchableColumns;
};
